import { 
  Box, 
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState, useEffect } from 'react';

import { IMultiSectionUnitConfiguration, IPropertyRange, IUnitBaseConfiguration } from "../../../Models/IUnitConfiguration";
import { Constants, PropertyConstants } from "../../../Models/Constants";
import { ApiService } from "../../../Services/ApiService";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IUnitPropertySpecification } from "../../../Models/IUnitPropertySpecification";
import { getFlowTypeUnits, getPreferredUnits, setTemporaryState } from "../../../Services/Global";
import logger from '../../../Services/LoggingService';
import { createFlexStyles, createSpacingStyles, createContentStyles, createCardStyles } from '../../../theme/layoutStyles';

/**
 * Property Range Selection component for setting the range of properties for a unit configuration 
 * using Material UI components
 *
 * @return {*} 
 */
export const PropertyRangeSelection: React.FC = () => {
    console.log('✅ PropertyRangeSelection component loaded - SIMPLE VERSION (legacy approach)');
    
    const ctx = useMsal();
    const apiService = new ApiService(ctx);
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const spacingStyles = createSpacingStyles(theme);
    const contentStyles = createContentStyles(theme);
    const cardStyles = createCardStyles(theme);
    const navigate = useNavigate();
    const params = useParams();
    const state: any = useLocation().state;
    
    console.log('🔵 PropertyRangeSelection params:', params);

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userInputs, setUserInputs] = useState<IPropertyRange[]>([]);
    const [saveMessage, setSaveMessage] = useState<string>();
    const [unitConfiguration, setStateUnitConfiguration] = useState<IUnitBaseConfiguration>();

    const preferredUnit = getPreferredUnits().preferredUnits;
    const temperatureUnit = preferredUnit === 'us' ? '°F' : '°C';

    const [unitConfig] = useState<IMultiSectionUnitConfiguration>({
        industry: params.i,
        subIndustry: params.si,
        application: params.a,
        subApplication: params.sa,
        processStage: params.ps,
        productLine: params.pl,
        alfaLavalPosition: params.alp,
        sectionCount: Number.parseInt(params.sc as string),
        inputs: params.ip ? params.ip.split(',') : [],
        flowType: state?.unitConfiguration?.flowType || "mass"  //TODO: Check if this is correct
    });

    // Styles
    const styles = {
        container: {
            padding: theme.spacing(3),
            marginTop: theme.spacing(2),
        },
        contentWrapper: {
            width: '100%',
            maxWidth: '98%',
            margin: '0 auto',
        },
        header: {
            ...flexStyles.flexCenterBetween,
            width: '100%',
            ...spacingStyles.marginBottom3,
        },
        section: {
            ...cardStyles.sectionCard,
            ...spacingStyles.marginBottom3,
            borderLeft: `5px solid ${theme.palette.primary.main}`,
        },
        tableCell: {
            padding: theme.spacing(1),
        },
        propertyName: {
            fontWeight: 'bold',
            paddingRight: theme.spacing(5),
            textAlign: 'right',
            minWidth: '250px',
        },
        inputField: {
            minWidth: '150px',
        },
        checkboxCell: {
            paddingLeft: theme.spacing(5),
        }
    };

    useEffect(() => {
        // Set the inputs for the unit configuration
        async function setInputs() {
            setIsLoading(true);
            try {
                let inputs: IPropertyRange[] = [];
                const unitconfiguration = state?.unitConfiguration as IUnitBaseConfiguration;
                setStateUnitConfiguration(unitconfiguration);

                if (unitConfig.sectionCount === 1) {
                    const props = await getRangeValidations(unitconfiguration);
                    unitConfig.inputs?.forEach((c) => {
                        const currProp = props.find((x: any) => x.propertyName == c);
                        if (currProp) {
                            inputs.push(currProp);
                        }
                        else {
                            inputs.push({ propertyName: c });
                        }
                    });
                    setUserInputs(inputs);
                }
                else {
                    await getUnitPropertySpecification();
                }
            } catch (error) {
                logger.error('Failed to load property range data', error as Error, {
                    sectionCount: unitConfig.sectionCount,
                    industry: unitConfig.industry,
                    productLine: unitConfig.productLine
                }, {
                    component: 'PropertyRangeSelection',
                    method: 'setInputs'
                });
            } finally {
                setIsLoading(false);
            }
        }

        // Get the range validations for the properties of the unit configuration
        async function getRangeValidations(unitInput: IUnitBaseConfiguration) {
            const response = await apiService.getAsync(`settings/rangeValidation`, unitInput);
            if (!response.ok) {
                throw new Error(`Could not get range configurations from API. ${response.status} - ${response.statusText}`);
            }
            const result = await response.json();
            return result;
        }

        // Get the unit property specification for the unit configuration
        async function getUnitPropertySpecification() {
            const response = await apiService.getAsync("settings/unitpropertyspecifications", unitConfig);
            if (!response.ok) {
                if (response.status === 404) {
                    return;
                }
                throw new Error(`${response.status}`);
            }

            try {
                const result: IUnitPropertySpecification = await response.json();
                const unitconfiguration = state?.unitConfiguration as IUnitBaseConfiguration;

                if (result) {
                    // CRITICAL FIX: For multi-section, we need to fetch property ranges from database
                    // instead of using stale data from state?.unitConfiguration?.properties
                    // This ensures we get the latest saved property ranges from PropertyRange table
                    let savedPropertyRanges: IPropertyRange[] = [];
                    try {
                        // Convert unitConfig to IUnitBaseConfiguration format for getRangeValidations
                        const unitInputForRanges: IUnitBaseConfiguration = {
                            industry: unitConfig.industry,
                            subIndustry: unitConfig.subIndustry,
                            application: unitConfig.application,
                            subApplication: unitConfig.subApplication,
                            processStage: unitConfig.processStage,
                            productLine: unitConfig.productLine,
                            alfaLavalPosition: unitConfig.alfaLavalPosition,
                            sectionCount: unitConfig.sectionCount,
                            flowType: unitConfig.flowType
                        };
                        savedPropertyRanges = await getRangeValidations(unitInputForRanges);
                        console.log('🔵 Loaded property ranges from database for multi-section:', savedPropertyRanges.length);
                    } catch (rangeError) {
                        logger.warn('Failed to load property ranges from database, using empty values', {
                            sectionCount: unitConfig.sectionCount,
                            industry: unitConfig.industry,
                            error: rangeError
                        }, {
                            component: 'PropertyRangeSelection',
                            method: 'getUnitPropertySpecification'
                        });
                    }

                    let inputs: IPropertyRange[] = [];
                    result.userInputProperties.push(PropertyConstants.TempApproachHotInlet);
                    result.userInputProperties.push(PropertyConstants.TempApproachHotOutlet);
                    result.userInputProperties.push(PropertyConstants.DeltaTemperatureColdSide);
                    result.userInputProperties.push(PropertyConstants.DeltaTemperatureHotSide);
                    result.userInputProperties?.forEach((c) => {
                        // Use property ranges from database (savedPropertyRanges) instead of stale state
                        // This ensures we show the latest saved values from PropertyRange table
                        const currProp = savedPropertyRanges.find((x: any) => x.propertyName == c);

                        if (currProp) {
                            inputs.push(currProp);
                        }
                        else {
                            inputs.push({ propertyName: c });
                        }
                    });

                    setUserInputs(inputs);
                }
            }
            catch (error) {
                logger.warn('Failed to parse unit property specification JSON', {
                    sectionCount: unitConfig.sectionCount,
                    industry: unitConfig.industry
                }, {
                    component: 'PropertyRangeSelection',
                    method: 'getUnitPropertySpecification'
                });
            }
        }

        setInputs();
    }, []);

    /**
     * Handle navigation to the unit configuration editor
     *
     */
    const handleNavigation = () => {
        navigate('/admin/UnitConfigurationManagement/unitconfigurationeditor', { 
            state: { 
                unitConfiguration: unitConfiguration == undefined ? state?.unitConfiguration : unitConfiguration 
            } 
        });
    };

    // Type guard function to check if a value is empty (undefined, empty string, or NaN)
    // CRITICAL: Empty fields represent "no additional rules", not the numeric value 0
    // This matches legacy behavior where empty fields are treated as undefined, not 0
    function isEmptyString(value: any | undefined): value is undefined {
        return value === undefined 
            || value === null
            || (typeof value === 'string' && value.trim() === '') 
            || (typeof value === 'number' && isNaN(value));
    }

    /**
     * Handle the submit of the property range selection form 
     *
     * @return {*} 
     */
    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            // Same approach as legacy: use userInputs directly
            let propertyRanges = userInputs ?? [];
            
            // Process values EXACTLY like legacy - convert empty strings to undefined
            // CRITICAL: After processing, ensure that empty values are truly undefined, not 0 or NaN
            for (let i = 0; i < propertyRanges.length; i++) {
                let current = propertyRanges[i];
                
                // Log before processing for debugging
                if (current.propertyName === 'TempApproachHotOutlet') {
                    console.log('🔵 BEFORE processing TempApproachHotOutlet:', {
                        lessThan: current.lessThan,
                        greaterThan: current.greaterThan,
                        lessThanType: typeof current.lessThan,
                        greaterThanType: typeof current.greaterThan
                    });
                }
                
                // Process lessThan: if empty/NaN, set to undefined; otherwise convert to number
                if (isEmptyString(current.lessThan)) {
                    current.lessThan = undefined;
                } else {
                    const numValue = Number(current.lessThan);
                    current.lessThan = isNaN(numValue) ? undefined : numValue;
                }
                
                // Process greaterThan: if empty/NaN, set to undefined; otherwise convert to number
                if (isEmptyString(current.greaterThan)) {
                    current.greaterThan = undefined;
                } else {
                    const numValue = Number(current.greaterThan);
                    current.greaterThan = isNaN(numValue) ? undefined : numValue;
                }
                
                current.isDecimal = current.isDecimal ?? true;
                
                // Log after processing for debugging
                if (current.propertyName === 'TempApproachHotOutlet') {
                    console.log('🟢 AFTER processing TempApproachHotOutlet:', {
                        lessThan: current.lessThan,
                        greaterThan: current.greaterThan,
                        lessThanType: typeof current.lessThan,
                        greaterThanType: typeof current.greaterThan,
                        lessThanIsUndefined: current.lessThan === undefined,
                        greaterThanIsUndefined: current.greaterThan === undefined
                    });
                }
            }
            
            if (propertyRanges.length != 0) {
                // CRITICAL: Only validate that lessThan is less than greaterThan when BOTH values are valid numbers
                // Empty fields (undefined) represent "no additional rules", not the numeric value 0
                // This matches legacy behavior: validation only compares when both values are provided
                // We must also check that values are not NaN, as NaN comparisons always return false
                const incorrectEntries = propertyRanges.filter(x => {
                    const lessThan = x.lessThan;
                    const greaterThan = x.greaterThan;
                    
                    // Debug logging to understand what's happening
                    // CRITICAL: Calculate values first, then use in logging
                    const bothDefined = lessThan != undefined && greaterThan != undefined;
                    const bothValidNumbers = bothDefined && 
                        !isNaN(lessThan!) && !isNaN(greaterThan!) && 
                        typeof lessThan === 'number' && typeof greaterThan === 'number';
                    const willFail = bothValidNumbers && lessThan! > greaterThan!;
                    
                    if (x.propertyName === 'TempApproachHotOutlet') {
                        console.log('🔍 TempApproachHotOutlet validation check:', {
                            propertyName: x.propertyName,
                            lessThan,
                            greaterThan,
                            lessThanType: typeof lessThan,
                            greaterThanType: typeof greaterThan,
                            lessThanIsNaN: lessThan !== undefined && isNaN(lessThan),
                            greaterThanIsNaN: greaterThan !== undefined && isNaN(greaterThan),
                            lessThanIsUndefined: lessThan === undefined,
                            greaterThanIsUndefined: greaterThan === undefined,
                            bothDefined,
                            bothValidNumbers,
                            comparison: bothDefined && typeof lessThan === 'number' && typeof greaterThan === 'number' 
                                ? `${lessThan} > ${greaterThan} = ${lessThan! > greaterThan!}` 
                                : 'N/A (skipped - not both valid numbers)',
                            willFail
                        });
                    }
                    
                    // Only compare if both are valid numbers
                    return willFail;
                });

                // Only show error if there are incorrect relationships (not for empty fields)
                if (incorrectEntries.length > 0) {
                    const incorrectPropertyNames = incorrectEntries.map(entry => entry.propertyName);
                    const errorMessage = "Less than value should be less than greater than value for properties: " + incorrectPropertyNames.join(', ') + ".";
                    
                    setTemporaryState(errorMessage, setSaveMessage);
                    setIsSaving(false);
                    return;
                }
            }
     
            // Filter out properties with both values empty (same as backend does in UpsertPropertyRange line 1421-1423)
            // Backend filters: p => p.PropertyName != null && (p.LessThan.HasValue || p.GreaterThan.HasValue)
            const filteredPropertyRanges = propertyRanges.filter(p => 
                p.propertyName != null && 
                (p.lessThan !== undefined || p.greaterThan !== undefined)
            );
            
            // CRITICAL: If all properties are empty after filtering, don't send request at all
            // Backend will overwrite existing properties with empty array if we send empty array
            // This prevents data corruption - user can leave fields empty, but if ALL are empty, no save happens
            if (filteredPropertyRanges.length === 0) {
                setTemporaryState("ℹ️ No properties to save - all fields are empty. Existing properties will remain unchanged.", setSaveMessage);
                setIsSaving(false);
                return;
            }
            
            // Build payload with only the fields that PropertyRangeUnitConfiguration expects
            // CRITICAL: Ensure no null/undefined values are sent to prevent JSON serialization issues
            // Backend serializes config.Data (line 663), and null values can cause ",null" to be appended to JSON
            // Clean all properties to ensure no null/undefined values are included
            
            // Clean properties array - CRITICAL: For properties that exist in filteredPropertyRanges, 
            // we MUST include both lessThan and greaterThan fields, even if they are null/undefined
            // This ensures backend knows we want to set null (clear the value) rather than keep existing value
            // Backend uses ?? operator which preserves existing value if incoming is null
            // So we must explicitly send null to clear values, not omit the field
            const cleanProperties = filteredPropertyRanges
                .filter(p => p != null && p.propertyName != null) // Remove any null entries
                .map((p: any) => {
                    const clean: any = { propertyName: p.propertyName };
                    
                    // CRITICAL: Always include lessThan and greaterThan fields, even if undefined/null
                    // This ensures backend can distinguish between "keep existing" and "set to null"
                    // If field is undefined/null, send null explicitly to clear the value in database
                    if (p.lessThan !== undefined && p.lessThan !== null && !isNaN(p.lessThan)) {
                        clean.lessThan = p.lessThan;
                    } else {
                        // Explicitly set to null to clear the value in database
                        clean.lessThan = null;
                    }
                    
                    if (p.greaterThan !== undefined && p.greaterThan !== null && !isNaN(p.greaterThan)) {
                        clean.greaterThan = p.greaterThan;
                    } else {
                        // Explicitly set to null to clear the value in database
                        clean.greaterThan = null;
                    }
                    
                    // isDecimal defaults to true if not specified
                    clean.isDecimal = p.isDecimal !== undefined && p.isDecimal !== null ? p.isDecimal : true;
                    return clean;
                });
            
            // Build payload with clean values only - no null/undefined
            let payload: any = {
                industry: unitConfig.industry || '',
                subIndustry: unitConfig.subIndustry || '',
                application: unitConfig.application || '',
                subApplication: unitConfig.subApplication || '',
                processStage: unitConfig.processStage || '',
                productLine: unitConfig.productLine || '',
                alfaLavalPosition: unitConfig.alfaLavalPosition || '',
                sectionCount: unitConfig.sectionCount || 0,
                flowType: unitConfig.flowType || '',
                inputs: Array.isArray(unitConfig.inputs) ? unitConfig.inputs : [],
                properties: cleanProperties  // Clean properties array with no null/undefined
            };
            
            // Final cleanup: Remove any keys with null/undefined values (shouldn't happen, but safety check)
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined || payload[key] === null) {
                    console.warn(`⚠️ Removing null/undefined field from payload: ${key}`);
                    delete payload[key];
                }
            });
            
            // Verify payload structure before sending
            // NOTE: We now explicitly send null values to clear fields in database
            // So we expect null values in the payload (they are intentional)
            const payloadString = JSON.stringify(payload);
            if (payloadString.includes('undefined')) {
                console.error('❌ Payload contains undefined values!', payload);
                setTemporaryState("Error: Payload contains invalid values. Please check the console.", setSaveMessage);
                setIsSaving(false);
                return;
            }
            
            console.log('🔵 Payload being sent:', payloadString);
            console.log('🔵 Clean properties count:', cleanProperties.length, 'Total properties:', propertyRanges.length);

            let response = await apiService.putAsync(payload, `settings/propertyRanges`);

            if (!response.ok) {
                setIsSaving(false);
                const errorText = await response.text();
                setTemporaryState(`Failed to save: ${errorText}`, setSaveMessage);
                logger.error('Failed to save property ranges', new Error(errorText), {
                    status: response.status,
                    payload: payload
                }, {
                    component: 'PropertyRangeSelection',
                    method: 'handleSubmit'
                });
                return;
            }

            const result: IUnitBaseConfiguration = await response.json();
            setStateUnitConfiguration({ ...unitConfiguration, properties: propertyRanges });
            setUserInputs(propertyRanges); // Update state with saved data (backend may filter out empty properties)
            setTemporaryState("✅ Saved", setSaveMessage);
            setIsSaving(false);
        } catch (error) {
            setIsSaving(false);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setTemporaryState(`Failed to save: ${errorMessage}`, setSaveMessage);
            logger.error('Error saving property ranges', error as Error, {}, {
                component: 'PropertyRangeSelection',
                method: 'handleSubmit'
            });
        }
    }

    /**
     * Handle the change of the property range selection form
     *
     * @param {string} property
     * @param {string} column
     * @param {*} e
     */
    const onChange = (property: string, column: string, e: any) => {
        setUserInputs(prevInputs => {
            const currentInputs = [...prevInputs!];

            const existingProperty = currentInputs.find((c: IPropertyRange) => c.propertyName === property);

            // CRITICAL: Handle empty strings as undefined, not NaN
            // Empty fields represent "no additional rules", not the numeric value 0
            // This matches legacy behavior and prevents validation errors
            const inputValue = e.target.value;
            const isEmpty = inputValue === '' || inputValue === null || inputValue === undefined;
            const numericValue = isEmpty ? undefined : Number(inputValue);
            // If Number() returns NaN (e.g., for invalid input), treat as undefined
            const finalValue = (numericValue !== undefined && isNaN(numericValue)) ? undefined : numericValue;

            if (existingProperty) {
                if (column === Constants.LessThan) {
                    existingProperty.lessThan = finalValue;
                } else if (column === Constants.GreaterThan) {
                    existingProperty.greaterThan = finalValue;
                }
                else {
                    existingProperty.isDecimal = e.target.checked;
                    resetMinMaxValuesForRow(property);
                }
            } else {
                let propRange: IPropertyRange = {
                    propertyName: property
                };
                if (column === Constants.LessThan) {
                    propRange.lessThan = finalValue;
                } else if (column === Constants.GreaterThan) {
                    propRange.greaterThan = finalValue;
                }
                else {
                    propRange.isDecimal = e.target.checked;
                    resetMinMaxValuesForRow(property);
                }
                currentInputs.push(propRange);
            }
            return currentInputs;
        });
    };

    /**
     * Reset the min max values for a row in the property range selection form 
     *
     * @param {string} property
     */
    const resetMinMaxValuesForRow = (property: string) => {
        setUserInputs((userInputs) =>
            userInputs?.map((input) => {
                if (input?.greaterThan && input?.lessThan) {
                    if ((input?.propertyName === property && input.isDecimal === false) && (input?.greaterThan.toString()?.indexOf('.') > -1 && input?.lessThan.toString()?.indexOf('.') > -1)) {
                        return { ...input, lessThan: 0, greaterThan: 0 }
                    }
                    else if (input.propertyName === property && input.isDecimal === false && input?.lessThan!.toString()?.indexOf('.') > -1) {
                        return { ...input, lessThan: 0, greaterThan: input.greaterThan }
                    }
                    else if (input.propertyName === property && input.isDecimal === false && input?.greaterThan!.toString()?.indexOf('.') > -1) {
                        return { ...input, lessThan: input.lessThan, greaterThan: 0 }
                    }
                    else {
                        return input;
                    }
                }
                else {
                    return input;
                }
            })
        );
    };

    /**
     * Get the units for a property - simple function like in legacy
     */
    function getUnits(prop: string) {
        let unit = '';
        if (prop.toLowerCase().includes('flow')) {
            unit = getFlowTypeUnits(unitConfiguration?.flowType!)
        } else if (prop.toLowerCase().includes('temp')) {
            unit = temperatureUnit;
        }
        return unit;
    }

    return (
        <Box sx={styles.container}>
            {isLoading ? (
                <Box sx={contentStyles.loadingState}>
                    <CircularProgress size={40} />
                    <Typography variant="body2">Loading...</Typography>
                </Box>
            ) : (
                <Stack spacing={3} sx={styles.contentWrapper}>
                    <Box sx={styles.header}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleNavigation}
                        >
                            Back
                        </Button>
                        <Box sx={flexStyles.flexCenter}>
                            {isSaving && (
                                <Box sx={{ ...flexStyles.flexCenter, mr: 2 }}>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    <Typography variant="body2">Saving...</Typography>
                                </Box>
                            )}
                            {saveMessage && (
                                <Typography variant="body2" sx={{ mr: 2 }}>
                                    {saveMessage}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                Save all information
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Property Range Selection
                        </Typography>
                    </Box>

                    <Paper sx={styles.section}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...styles.tableCell, ...styles.propertyName }}>Property names</TableCell>
                                        <TableCell sx={styles.tableCell}>Minimum value</TableCell>
                                        <TableCell sx={styles.tableCell}>Maximum value</TableCell>
                                        <TableCell sx={{ ...styles.tableCell, ...styles.checkboxCell }}>Allow decimal value</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userInputs && userInputs.map((input, index) => {
                                        if (!input.propertyName) return null;
                                        // Same approach as legacy: find current input for value
                                        const currentInput = userInputs.find(x => x.propertyName === input.propertyName);
                                        return (
                                            <TableRow key={input.propertyName}>
                                                <TableCell sx={{ ...styles.tableCell, ...styles.propertyName }}>
                                                    {input.propertyName} ({getUnits(input.propertyName)})
                                                </TableCell>
                                                <TableCell sx={styles.tableCell}>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={styles.inputField}
                                                        value={currentInput?.lessThan !== undefined && currentInput?.lessThan !== null ? currentInput.lessThan.toString() : ''}
                                                        onChange={(e) => onChange(input.propertyName, Constants.LessThan, e)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={styles.tableCell}>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={styles.inputField}
                                                        value={currentInput?.greaterThan !== undefined && currentInput?.greaterThan !== null ? currentInput.greaterThan.toString() : ''}
                                                        onChange={(e) => onChange(input.propertyName, Constants.GreaterThan, e)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ ...styles.tableCell, ...styles.checkboxCell }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={currentInput?.isDecimal ?? true}
                                                                onChange={(e) => onChange(input.propertyName, "allowDecimal", e)}
                                                            />
                                                        }
                                                        label=""
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Stack>
            )}
        </Box>
    );
}; 